import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Timer, Sparkles, CheckCircle2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { api, formatError, streamPost, t } from "@/lib/api";
import { AiText, ModelPicker } from "@/components/Common";

function useCountdown(seconds, key) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    setLeft(seconds);
    const t = setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
    return () => clearInterval(t);
  }, [seconds, key]);
  return left;
}

function Summary({ w, topic, answers }) {
  const [model, setModel] = useState("claude");
  const [ai, setAi] = useState("");
  const [loading, setLoading] = useState(false);
  const critique = async () => {
    setLoading(true); setAi("");
    const context = `${t("Atelier", "Workshop")} ${w.title} — « ${topic} »\n` + w.steps.map((s, i) => `${s.title} : ${answers[i] || t("(vide)", "(empty)")}`).join("\n");
    try { await streamPost("/ai/critique", { context, model }, setAi); } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  return (
    <div className="fade-up" data-testid="workshop-summary">
      <div className="flex items-center gap-3 text-emerald-600"><CheckCircle2 className="h-6 w-6" /><span className="font-semibold">{t("Atelier terminé · +30 pts", "Workshop completed · +30 pts")}</span></div>
      <h1 className="mt-4 font-display text-4xl font-bold">{topic}</h1>
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {w.steps.map((s, i) => (
          <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600">{s.title}</div>
            <p className="mt-2 text-sm whitespace-pre-wrap text-slate-700">{answers[i] || <span className="text-slate-400">—</span>}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-3xl border border-rose-200 bg-rose-50/60 p-6">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="font-display text-lg font-semibold">{t("Demander au contradicteur IA", "Ask the AI challenger")}</div>
          <ModelPicker value={model} onChange={setModel} />
        </div>
        <p className="mt-1 text-sm text-slate-500">{t("L'IA ne donne pas la réponse : elle pointe vos angles morts.", "The AI won't give you the answer: it points out your blind spots.")}</p>
        <button data-testid="workshop-ai-critique-button" onClick={critique} disabled={loading} className="mt-4 inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60 transition-colors">
          <Sparkles className="h-4 w-4" /> {loading ? t("Réflexion…", "Thinking…") : t("Me challenger", "Challenge me")}
        </button>
        {ai && <div className="mt-5 rounded-2xl bg-white p-5"><AiText text={ai} testId="workshop-ai-critique-output" /></div>}
      </div>
      <Link to="/app/ateliers" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600"><ArrowLeft className="h-4 w-4" /> {t("Retour aux ateliers", "Back to workshops")}</Link>
    </div>
  );
}

export default function WorkshopRunner() {
  const { id } = useParams();
  const [w, setW] = useState(null);
  const [topic, setTopic] = useState("");
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);
  const startAt = useRef(0);
  useEffect(() => { api.get(`/workshops/${id}`).then(({ data }) => { setW(data); setAnswers(data.steps.map(() => "")); }); }, [id]);
  const left = useCountdown(w?.steps[step]?.seconds || 0, `${step}-${started}`);
  if (!w) return null;
  if (done) return <Summary w={w} topic={topic} answers={answers} />;

  if (!started) return (
    <div className="max-w-2xl fade-up">
      <Link to="/app/ateliers" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600"><ArrowLeft className="h-4 w-4" /> {t("Ateliers", "Workshops")}</Link>
      <div className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600">{w.method} · {w.duration} min</div>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold">{w.title}</h1>
      <p className="mt-4 text-slate-600">{w.summary}</p>
      <ol className="mt-8 space-y-2">{w.steps.map((s, i) => <li key={s.title} className="flex gap-3 text-sm"><span className="font-mono text-slate-400">{String(i + 1).padStart(2, "0")}</span>{s.title}</li>)}</ol>
      <label className="mt-10 block text-sm font-semibold">{t("Sur quel sujet voulez-vous travailler ?", "Which topic do you want to work on?")}</label>
      <Input data-testid="workshop-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={t("Ex. : notre réunion d'équipe du lundi", "E.g. our Monday team meeting")} className="mt-2 h-12" />
      <button data-testid="workshop-start-button" disabled={!topic.trim()} onClick={() => { setStarted(true); startAt.current = Date.now(); }}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-7 py-3 font-semibold text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors">
        {t("Démarrer", "Start")} <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );

  const s = w.steps[step];
  const last = step === w.steps.length - 1;
  const finish = async () => {
    try {
      await api.post(`/workshops/${id}/complete`, { topic, answers, duration_seconds: Math.round((Date.now() - startAt.current) / 1000) });
      toast.success(t("Atelier terminé ! +30 points", "Workshop completed! +30 points"));
      setDone(true);
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-2">
        {w.steps.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-indigo-600" : "bg-slate-200"}`} />)}
      </div>
      <div className="mt-8 flex items-center justify-between">
        <span className="font-mono text-sm text-slate-400">{t("Étape", "Step")} {step + 1}/{w.steps.length}</span>
        <span data-testid="workshop-timer" className={`flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-sm ${left === 0 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-700"}`}>
          <Timer className="h-4 w-4" /> {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}
        </span>
      </div>
      <div key={step} className="fade-up">
        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold" data-testid="workshop-step-title">{s.title}</h2>
        <p className="mt-3 text-lg text-slate-600">{s.prompt}</p>
        <Textarea data-testid="workshop-step-answer" value={answers[step]} autoFocus rows={7} className="mt-6 text-base bg-white"
          onChange={(e) => setAnswers(answers.map((a, i) => (i === step ? e.target.value : a)))} placeholder={t("Notez tout ce qui vous vient, sans filtrer…", "Write down whatever comes to mind, without filtering…")} />
      </div>
      <div className="mt-6 flex justify-between">
        <button data-testid="workshop-prev-button" disabled={step === 0} onClick={() => setStep(step - 1)} className="rounded-full px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-30 transition-colors">{t("Précédent", "Previous")}</button>
        {last ? (
          <button data-testid="workshop-finish-button" onClick={finish} className="rounded-full bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors">{t("Terminer l'atelier", "Finish workshop")}</button>
        ) : (
          <button data-testid="workshop-next-button" onClick={() => setStep(step + 1)} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors">{t("Suivant", "Next")} <ArrowRight className="h-4 w-4" /></button>
        )}
      </div>
    </div>
  );
}
